import { getThreeObject } from './ObjectManager'
import { registry } from '../components-lib/registry'
import { resolveUniformScaledParams } from '../utils/uniformScaleParams'

export type AlignMode =
  | 'left' | 'centerH' | 'right'
  | 'top' | 'centerV' | 'bottom'
  | 'distributeH' | 'distributeV'
  | 'sameWidth' | 'sameDepth'

interface ObjBounds {
  id: string
  x: number   // 世界坐标中心 X
  y: number   // 世界坐标原点 Y（对齐时保留）
  z: number   // 世界坐标中心 Z
  w: number   // 宽度
  d: number   // 深度
  h: number   // 高度
  minX: number
  maxX: number
  minZ: number
  maxZ: number
}

function getObjBounds(id: string, sceneObjects: any[]): ObjBounds | null {
  const obj3d = getThreeObject(id)
  if (!obj3d) return null
  const sceneObj = sceneObjects.find(o => o.id === id)
  if (!sceneObj) return null
  const def = registry.get(sceneObj.type)
  if (!def) return null

  const bounds = def.getBounds(resolveUniformScaledParams(def, sceneObj.params))
  const cx = sceneObj.position[0]
  const cy = sceneObj.position[1]
  const cz = sceneObj.position[2]
  const hw = bounds.w / 2
  const hd = bounds.d / 2

  return {
    id,
    x: cx, y: cy, z: cz,
    w: bounds.w, d: bounds.d, h: bounds.h,
    minX: cx - hw, maxX: cx + hw,
    minZ: cz - hd, maxZ: cz + hd,
  }
}

/**
 * 执行对齐/等距/同尺寸操作
 * @returns 每个对象变化后的新位置/参数（供调用者写入 store）
 */
export function applyAlignment(
  ids: string[],
  mode: AlignMode,
  sceneObjects: any[]
): { id: string; position?: [number, number, number]; params?: Record<string, any> }[] {
  if (ids.length < 2) return []

  const allBounds = ids.map(id => getObjBounds(id, sceneObjects)).filter(Boolean) as ObjBounds[]
  if (allBounds.length < 2) return []

  const results: { id: string; position?: [number, number, number]; params?: Record<string, any> }[] = []

  switch (mode) {
    case 'left': {
      // 以最左侧对象的左边界为基准
      const minX = Math.min(...allBounds.map(b => b.minX))
      allBounds.forEach(b => {
        const newCx = minX + b.w / 2
        results.push({ id: b.id, position: [newCx, b.y, b.z] })
      })
      break
    }
    case 'right': {
      const maxX = Math.max(...allBounds.map(b => b.maxX))
      allBounds.forEach(b => {
        const newCx = maxX - b.w / 2
        results.push({ id: b.id, position: [newCx, b.y, b.z] })
      })
      break
    }
    case 'centerH': {
      const minX = Math.min(...allBounds.map(b => b.minX))
      const maxX = Math.max(...allBounds.map(b => b.maxX))
      const cx = (minX + maxX) / 2
      allBounds.forEach(b => {
        results.push({ id: b.id, position: [cx, b.y, b.z] })
      })
      break
    }
    case 'top': {
      // Z 轴最小（前）对齐
      const minZ = Math.min(...allBounds.map(b => b.minZ))
      allBounds.forEach(b => {
        const newCz = minZ + b.d / 2
        results.push({ id: b.id, position: [b.x, b.y, newCz] })
      })
      break
    }
    case 'bottom': {
      const maxZ = Math.max(...allBounds.map(b => b.maxZ))
      allBounds.forEach(b => {
        const newCz = maxZ - b.d / 2
        results.push({ id: b.id, position: [b.x, b.y, newCz] })
      })
      break
    }
    case 'centerV': {
      const minZ = Math.min(...allBounds.map(b => b.minZ))
      const maxZ = Math.max(...allBounds.map(b => b.maxZ))
      const cz = (minZ + maxZ) / 2
      allBounds.forEach(b => {
        results.push({ id: b.id, position: [b.x, b.y, cz] })
      })
      break
    }
    case 'distributeH': {
      // 水平等距：按 X 排序，保持最左和最右对象不动，中间等距
      const sorted = [...allBounds].sort((a, b) => a.x - b.x)
      const totalSpan = sorted[sorted.length - 1].maxX - sorted[0].minX
      const totalWidth = sorted.reduce((s, b) => s + b.w, 0)
      const gap = (totalSpan - totalWidth) / (sorted.length - 1)

      let curX = sorted[0].minX
      sorted.forEach((b, i) => {
        if (i === 0) {
          results.push({ id: b.id, position: [curX + b.w / 2, b.y, b.z] })
          curX += b.w + gap
        } else {
          results.push({ id: b.id, position: [curX + b.w / 2, b.y, b.z] })
          curX += b.w + gap
        }
      })
      break
    }
    case 'distributeV': {
      // 垂直等距（Z轴）
      const sorted = [...allBounds].sort((a, b) => a.z - b.z)
      const totalSpan = sorted[sorted.length - 1].maxZ - sorted[0].minZ
      const totalDepth = sorted.reduce((s, b) => s + b.d, 0)
      const gap = (totalSpan - totalDepth) / (sorted.length - 1)

      let curZ = sorted[0].minZ
      sorted.forEach((b) => {
        results.push({ id: b.id, position: [b.x, b.y, curZ + b.d / 2] })
        curZ += b.d + gap
      })
      break
    }
    case 'sameWidth': {
      // 以第一个选中对象的宽度为基准
      const refWidth = allBounds[0].w
      allBounds.forEach(b => {
        results.push({ id: b.id, params: { width: refWidth } })
      })
      break
    }
    case 'sameDepth': {
      const refDepth = allBounds[0].d
      allBounds.forEach(b => {
        results.push({ id: b.id, params: { depth: refDepth } })
      })
      break
    }
  }

  return results
}
